/**
 * §8's sign-in page.
 *
 * The page has one job beyond starting the sign-in: **say which of five things is true**, because the
 * instance says all of them the same way and only the response body separates them. So the headline,
 * the explanation and the button label are three parallel tables over `SessionStatus`, and every one of
 * them is exhaustive by the compiler in `model.ts`.
 *
 * This is the one page where the *permissions* are the subject rather than an implementation detail:
 * the instance's own approval screen lists them verbatim, and a reader who cannot tell what they permit
 * either approves blindly or does not approve at all. What is kept out is everything about how the
 * credential is carried — headers, keys, statuses. What a reader owns is the session: where it is
 * stored, what it can do, when it runs out, and what signing out does and does not reach.
 *
 * The session readout is a **label/value list, never a sentence**, and that is 4e's rule applied
 * before 4e existed: "Expires · in 27 days" cannot mix an English verb with a locale-formatted phrase
 * the way "Updated vor 2 Wochen" did, because there is no verb to mix.
 */
export const login = {
  headline: {
    checking: 'Checking this browser for a session…',
    signedInAnonymous: 'Signed in to this instance',
    signedInAs: (username: string): string => `Signed in as ${username}`,
    expired: 'This session has expired',
    unreadable: 'The stored session cannot be read',
    signedOut: 'Sign in to your instance',
    storeFailed: 'This browser would not save the session',
    emptyToken: 'The instance sent nothing back',
    denied: 'Nothing was approved',
  },

  explanation: {
    /** What stands in for the host before §3 has resolved one. */
    theInstance: 'the instance',
    active: (host: string): string =>
      `You are signed in to ${host}. The session is stored in this browser only, for this instance only, and signing out deletes it.`,
    underScoped: (host: string): string =>
      `Your session on ${host} works, but it does not cover everything this version of Visious asks for. Anything below marked "not granted" will be refused until you sign in again.`,
    expired:
      'Sessions run out after a while and this one has. Signing in again is all it takes, and nothing about your account changes.',
    unreadable: (host: string): string =>
      `There is a session stored for ${host} that Visious cannot read. Signing in again replaces it.`,
    signedOut: (host: string): string =>
      `Subscriptions, your playlists and history live on ${host}, not in Visious. Signing in sends you to the instance's own approval page. Visious never sees your password, only what the instance hands back.`,
  },

  notice: {
    /** A session that arrived unasked-for signs the reader into **somebody else's** account. */
    unsolicited:
      'That link carried a sign-in for an account, and nothing in this tab had asked for one, so it was thrown away instead of saved. A link like that signs you into somebody else\'s account, where everything you then watch is recorded. Start signing in from this page instead.',
    instanceMismatch: (granted: string, current: string): string =>
      `That approval came back for ${granted}, but Visious is now talking to ${current}. Nothing was saved: a session from one instance is never valid on another.`,
    storeFailed:
      'The instance signed you in and this browser would not keep it. That means private browsing with storage blocked, or a full disk. Signing in again will do the same, so the storage permission for this site is what has to change.',
    verifyFailed: (title: string, detail: string): string => `${title}. ${detail}`,
    emptyToken:
      'The instance sent you back without a session and without a refusal, so there was nothing to save. Trying again is worth one attempt. If it repeats, the instance is not signing anyone in.',
    /*
     * Deliberately covers both ways to arrive here, because they are indistinguishable and a browser
     * pass found the second: the instance's "No, thanks" *and* leaving the consent page by pressing
     * Back. An earlier wording said "the instance sent you back", which is a false clause about the
     * second — a bfcache restore is not a mount, so the record of a started sign-in outlives it.
     */
    denied:
      'A sign-in was started and nothing came back. Either it was declined on the instance, or the approval page was left without approving. Nothing was granted and nothing was saved.',
    underScoped: (missing: number, total: number): string =>
      `This session is missing ${missing} of the ${total} permissions Visious asks for, so the pages that need them will be refused. Signing in again asks for the whole list and replaces it.`,
    expiringSoon:
      'This session expires soon. Signing in again now costs two clicks and avoids a feed that stops loading later.',
  },

  signIn: {
    signedOut: 'Sign in on the instance',
    expired: 'Sign in again',
    unreadable: 'Replace the stored session',
    underScoped: 'Sign in again with every permission',
    active: 'Sign in again',
  },

  /**
   * R9's permission list in words, keyed by the *endpoint* rather than by the formatted scope string —
   * so adding a method to the list does not silently drop its explanation.
   *
   * This is the consent screen's counterpart, not decoration: the instance's own approval page lists
   * `GET;POST;DELETE:subscriptions*` verbatim, and a reader who cannot tell what that permits either
   * approves blindly or does not approve at all.
   */
  permission: {
    subscriptions: {
      title: 'Your subscriptions',
      detail: 'Read which channels you follow, and subscribe or unsubscribe from inside Visious.',
    },
    playlists: {
      title: 'Your playlists',
      detail: 'Read your own and private playlists, create and rename them, and add or remove videos.',
    },
    feed: {
      title: 'Your subscriptions feed',
      detail: 'Read the list of new videos from the channels you follow.',
    },
    preferences: {
      title: 'Your instance preferences',
      detail: 'Read the settings stored on the instance, and write back the ones Visious mirrors.',
    },
    history: {
      title: 'Your watch history',
      detail: 'Read what you have watched, record new watches, and remove entries.',
    },
    /** The opt-in sixth row — present only when automatic renewal is chosen or already held. */
    tokens: {
      title: 'Automatic renewal',
      detail:
        'Replace this session with a fresh one before it expires. It can only mint sessions limited to these same permissions, and can only end itself, never another session.',
    },
    /** A permission with no copy is one somebody added without a sentence: name it rather than hide it. */
    undescribed: 'No description for this permission yet.',
  },

  /** §3 resolving no instance is a *configuration* problem — there is nothing to sign in to. */
  noInstance: {
    title: 'Visious has no instance to sign in to',
    detail: 'No instance is configured, so there is nothing to sign in to. Choose one under Settings.',
    retryable: true,
  },

  /** What the one authenticated call can fail with, beyond the shared meanings of a refusal. */
  verify: {
    notFound: {
      title: 'This instance has no accounts',
      detail: 'It does not serve the account features Visious signs in for, so there is nothing to sign in to.',
      retryable: false,
    },
    unreadableTitle: 'The instance answered with something unexpected',
    unreadableDetail:
      'The call was allowed, so the session works, and what came back was not something Visious could read.',
    disabled: {
      title: 'This instance has switched its accounts off',
      detail: 'Signing in cannot help: its administrator turned the account features off.',
      retryable: false,
    },
  },

  page: {
    grantPanelLabel: 'Sign in',
    sessionPanelLabel: 'This session',
    sessionHeading: 'This session',
    permissionsPanelLabel: 'Permissions',
    /** Not a disclaimer — both halves are properties of the design a reader can check. */
    footnoteBefore: 'Visious never sees your instance password. The approval happens on',
    footnoteAfter: 'itself, and what comes back is limited to the permissions above.',
    /** The honest phrasing for a destination the nav does not describe. */
    whereYouWere: 'where you were',
    returnAfter: (destination: string): string =>
      `You will come straight back to ${destination} afterwards.`,
    continueTo: (destination: string): string => `Continue to ${destination}`,
    checkingSession: 'Checking this browser for an existing session…',
    factAccount: 'Account',
    factInstance: 'Instance',
    factExpires: 'Expires',
    factGranted: 'Granted',
    /** What stands in for a fact the session does not carry. */
    factMissing: 'Not known',
    signOut: 'Sign out',
    checkAgain: 'Check again',
    signOutNote:
      'Signing out deletes the session from this browser. With automatic renewal granted it also asks the instance to end it, and that permission covers ending its own session and no other. Without it, use the instance\'s own token list.',
    verifyChecking: 'Asking the instance whether this session still works…',
    verifyOkEmpty: 'The instance accepted it. You follow no channels.',
    verifyOk: (count: number): string =>
      `The instance accepted it. You follow ${count} channel${count === 1 ? '' : 's'}.`,
    verifyRefusedSignedOut:
      'The instance refused this session, so it is not usable. Signing in again replaces it.',
    verifyRefused: 'The instance refused this. The notice above says which of the reasons it gave.',
    permissionsHeadingGranted: 'What this session can do',
    permissionsHeadingWanted: 'What Visious will ask for',
    granted: 'granted',
    notGranted: 'not granted',
    scopeNoteBefore: 'This is the whole list. Visious does not ask for the instance-wide',
    scopeNoteAfter:
      'permission. Automatic renewal is the one addition you can choose, and it is a narrow one: it mints sessions limited to these same permissions and can end only itself.',
    /** The renewal option beside the sign-in button. The row above carries the honest trade-off. */
    renewLabel: 'Renew this session automatically',
    renewDetail:
      'Asks for one more permission, so the session can replace itself before its 30 days run out. Without it, Visious asks you to sign in again instead.',
  },
} as const;
